// v3/design-system/composites/ThemeToggle.tsx — Light/dark mode switch
// One brand theme (Meridian) with a designed dark mode: shadows off, borders on
// (tokens.css). This toggles only light/dark — never a brand/theme picker.
// Persisted in localStorage('v3-theme'); default follows the OS preference.
// Zero imports from v1/v2.

import { useCallback, useSyncExternalStore } from 'react';
import { Moon, Sun } from 'lucide-react';

import { cn } from '../utils';

const STORAGE_KEY = 'v3-theme';

/** Apply the stored (or OS-preferred) mode. Called once at v3 boot from App.tsx. */
export function initTheme(): void {
  const stored = localStorage.getItem(STORAGE_KEY);
  const dark =
    stored === 'dark' ||
    (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
}

/** Flip light/dark and persist. Safe to call from any menu item. */
export function toggleTheme(): boolean {
  const next = !document.documentElement.classList.contains('dark');
  document.documentElement.classList.toggle('dark', next);
  localStorage.setItem(STORAGE_KEY, next ? 'dark' : 'light');
  listeners.forEach((l) => l());
  return next;
}

let listeners: Array<() => void> = [];

function subscribe(cb: () => void) {
  listeners.push(cb);
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
}

function isDark(): boolean {
  return document.documentElement.classList.contains('dark');
}

export interface ThemeToggleProps {
  /** Set when the button sits on the navy chassis instead of the content plane */
  onChassis?: boolean;
  className?: string;
}

export function ThemeToggle({ onChassis, className }: ThemeToggleProps) {
  const dark = useSyncExternalStore(subscribe, isDark);

  const toggle = useCallback(() => {
    toggleTheme();
  }, []);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-fast',
        onChassis
          ? 'text-[var(--m-sidebar-fg)] hover:bg-white/10 hover:text-white'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
        className,
      )}
    >
      {dark ? <Sun className="h-[17px] w-[17px]" /> : <Moon className="h-[17px] w-[17px]" />}
    </button>
  );
}
